import { useEffect, useRef } from "react";
import { useViewerDomain } from "@/configurator/domain/hooks/useViewerDomain";
import type { RendererCommand } from "@/configurator/domain/models/types";
import { Scene3D } from "@/configurator/utils/scene/Scene3D";

/**
 * Fallback for the model-loaded event. The scene fires `modelLoaded` when the
 * GLB is in, but a config whose model fails to load would otherwise never get
 * its first configuration applied at all.
 */
const INITIAL_APPLY_FALLBACK_MS = 500;

function applyRendererCommand(scene: Scene3D, command: RendererCommand) {
  switch (command.method) {
    case "applyTexture":
      void scene.applyTexture(command.surface);
      break;
    case "switchMaterials":
      scene.switchMaterials(command.surface);
      break;
    case "applyVariant":
      scene.applyVariant(command.variantName);
      break;
    case "showPart":
      scene.showPart(command.partName, command.modelName);
      break;
    case "setPartVisibility":
      scene.setPartVisibility(command.modelName, command.visible);
      break;
    case "applyMaterial":
      scene.applyMaterial(command.materialId, command.target);
      break;
  }
}

function applyAll(scene: Scene3D, commands: RendererCommand[]) {
  for (const command of commands) applyRendererCommand(scene, command);
}

export type SceneRendererComponent = {
  mountRef: React.MutableRefObject<HTMLDivElement | null>;
  /** `null` renders the canvas transparent, over whatever the host page paints. */
  backgroundColor: string | null;
};

/**
 * Owns the lifetime of the 3D scene: creates one per model and scene config,
 * re-applies the configuration whenever the domain layer resolves a new set of
 * commands, and disposes the WebGL context on unmount.
 */
export function useSceneRendererComponent(
  onSceneReady?: (scene: Scene3D) => void,
): SceneRendererComponent {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const sceneRef = useRef<Scene3D | null>(null);
  const onSceneReadyRef = useRef(onSceneReady);
  const { activeModel, scene: sceneConfig, commands } = useViewerDomain();

  // Read by the mount effect, which must not re-run when the configuration
  // changes — only when the model or the scene setup does.
  const latestCommands = useRef(commands);
  latestCommands.current = commands;

  useEffect(() => {
    onSceneReadyRef.current = onSceneReady;
  }, [onSceneReady]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const scene = new Scene3D(mount, {
      modelPath: activeModel?.path,
      backgroundColor: sceneConfig.backgroundColor,
      contactShadow: sceneConfig.contactShadow,
      textureRepeat: sceneConfig.textureRepeat,
      textureTileSize: sceneConfig.textureTileSize,
    });
    sceneRef.current = scene;
    onSceneReadyRef.current?.(scene);

    const applyInitial = () => applyAll(scene, latestCommands.current);

    mount.addEventListener("modelLoaded", applyInitial);
    const timeoutId = setTimeout(applyInitial, INITIAL_APPLY_FALLBACK_MS);

    return () => {
      mount.removeEventListener("modelLoaded", applyInitial);
      clearTimeout(timeoutId);
      scene.dispose();
      sceneRef.current = null;
    };
  }, [
    activeModel?.path,
    sceneConfig.backgroundColor,
    sceneConfig.contactShadow,
    sceneConfig.textureRepeat,
    sceneConfig.textureTileSize,
  ]);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    applyAll(scene, commands);
  }, [commands]);

  return { mountRef, backgroundColor: sceneConfig.backgroundColor };
}
